import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DatePicker,
  Form,
  Input,
  Message,
  Modal,
  Select,
  Table,
  TimePicker,
  Tooltip,
} from "@arco-design/web-react";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";

import {
  cancelScheduledPostRequest,
  fetchScheduledPosts,
  updatePostRequest,
} from "../../../api/feed";
import { PostApi } from "../../DashBoard/partials/Feed/types/types";
import { createUpdatedPostData } from "../../../utils/createUpdatedPostData";
import { formatScheduledDateTime } from "../../../utils/meetingDateTime";
import { getButtonColor, getButtonTextColor } from "../../../utils/cssVariables";
import {
  combineScheduleToUtcISO,
  getBrowserTimezone,
  getPreferredEventTimezoneOptions,
  parseScheduleForForm,
} from "../../../utils/scheduleDateTime";
import { getScheduledStatsCol, getScheduledTableScrollX } from "./statsTableLayout";
import { useBreakpoint } from "../../../utils/hooks/useBreakpoint";

const SCHEDULED_PAGE_SIZE = 10;

type ScheduleEditValues = {
  heading: string;
  content: string;
  schedule_date?: string | Dayjs;
  schedule_time?: string | Dayjs;
  schedule_timezone?: string;
};

const previewText = (post: PostApi): string => {
  const title = (post.heading || "").trim();
  const body = (post.content || "").trim();
  const combined = title ? `${title} — ${body}` : body;
  return combined || "—";
};

export const ScheduledPostsSection = () => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint("mobile");
  const statsCol = useMemo(() => getScheduledStatsCol(isMobile), [isMobile]);
  const statsScrollX = useMemo(() => getScheduledTableScrollX(isMobile), [isMobile]);
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [editingPost, setEditingPost] = useState<PostApi | null>(null);
  const [form] = Form.useForm<ScheduleEditValues>();
  const timezoneOptions = useMemo(() => getPreferredEventTimezoneOptions(), []);

  const { data: scheduledPosts = [], isLoading, error } = useQuery({
    queryKey: ["admin-scheduled-posts"],
    queryFn: fetchScheduledPosts,
    retry: false,
  });

  useEffect(() => {
    if (error) {
      Message.error({
        content: t("admin.statistics.scheduled_load_error"),
        closable: true,
        duration: 4000,
      });
    }
  }, [error, t]);

  const cancelMutation = useMutation({
    mutationFn: cancelScheduledPostRequest,
    onSuccess: () => {
      Message.success(t("admin.statistics.scheduled_cancel_success"));
      queryClient.invalidateQueries({ queryKey: ["admin-scheduled-posts"] });
    },
    onError: () => {
      Message.error(t("admin.statistics.scheduled_cancel_error"));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ([postId, formData]: [number, FormData]) =>
      updatePostRequest([postId, formData]),
    onSuccess: () => {
      Message.success(t("admin.statistics.scheduled_update_success"));
      setEditingPost(null);
      queryClient.invalidateQueries({ queryKey: ["admin-scheduled-posts"] });
    },
    onError: () => {
      Message.error(t("admin.statistics.scheduled_update_error"));
    },
  });

  const openEdit = (post: PostApi) => {
    setEditingPost(post);
    const browserTz = getBrowserTimezone();
    if (post.publish_at) {
      const parsed = parseScheduleForForm(post.publish_at, browserTz);
      form.setFieldsValue({
        heading: post.heading,
        content: post.content,
        schedule_date: parsed.schedule_date,
        schedule_time: parsed.schedule_time,
        schedule_timezone: parsed.schedule_timezone,
      });
      return;
    }
    const when = dayjs().add(1, "hour");
    form.setFieldsValue({
      heading: post.heading,
      content: post.content,
      schedule_date: when,
      schedule_time: when,
      schedule_timezone: browserTz,
    });
  };

  const handleCancelPost = (post: PostApi) => {
    Modal.confirm({
      title: t("admin.statistics.scheduled_cancel_confirm_title"),
      content: t("admin.statistics.scheduled_cancel_confirm_body"),
      okText: t("admin.statistics.scheduled_cancel"),
      onOk: () => cancelMutation.mutateAsync(post.id),
    });
  };

  const handleSaveEdit = (values: ScheduleEditValues) => {
    if (!editingPost) return;
    const publishAt = combineScheduleToUtcISO(
      values.schedule_date,
      values.schedule_time,
      values.schedule_timezone || getBrowserTimezone()
    );
    if (!publishAt || !dayjs(publishAt).isAfter(dayjs())) {
      Message.error(t("home_page.schedule_datetime_future"));
      return;
    }
    const formData = createUpdatedPostData({
      post: editingPost,
      heading: values.heading,
      content: values.content,
      publish_at: publishAt,
    });
    updateMutation.mutate([editingPost.id, formData]);
  };

  const disablePastDates = (current: Dayjs) => {
    return current && current.isBefore(dayjs().startOf("day"));
  };

  const columns = useMemo(
    () => [
      {
        title: t("admin.statistics.scheduled_col_when"),
        dataIndex: "publish_at",
        width: statsCol.when,
        render: (value: string) => (
          <span className="statistics__moderation-date">
            {value ? formatScheduledDateTime(value) : "—"}
          </span>
        ),
      },
      {
        title: t("admin.statistics.scheduled_col_preview"),
        dataIndex: "heading",
        width: statsCol.preview,
        ellipsis: true,
        render: (_: string, record: PostApi) => {
          const full = previewText(record);
          return (
            <Tooltip content={full} position="tl">
              <span className="statistics__scheduled-preview">{full}</span>
            </Tooltip>
          );
        },
      },
      {
        title: isMobile
          ? t("admin.statistics.scheduled_col_by_short")
          : t("admin.statistics.scheduled_col_by"),
        dataIndex: "user_name",
        width: statsCol.by,
        ellipsis: false,
        render: (value: string) => value || "—",
      },
      {
        title: t("admin.statistics.scheduled_col_actions"),
        dataIndex: "id",
        width: statsCol.actions,
        fixed: isMobile ? ("right" as const) : undefined,
        render: (_: number, record: PostApi) => (
          <div className="statistics__scheduled-actions">
            <Button size="small" type="text" onClick={() => openEdit(record)}>
              {t("admin.statistics.scheduled_edit")}
            </Button>
            <Button
              size="small"
              type="text"
              loading={cancelMutation.isPending}
              onClick={() => handleCancelPost(record)}
            >
              {t("admin.statistics.scheduled_cancel")}
            </Button>
          </div>
        ),
      },
    ],
    [t, cancelMutation.isPending, statsCol, isMobile]
  );

  return (
    <div className="statistics__list statistics__scheduled-section">
      <div className="statistics__list-header">
        <h2 className="statistics__list-header-title">
          {t("admin.statistics.scheduled_posts_section_title")}
        </h2>
      </div>
      <div className="statistics__panel">
        <Table
          className="statistics__moderation-table"
          rowKey="id"
          loading={isLoading}
          data={scheduledPosts}
          columns={columns}
          scroll={{ x: statsScrollX }}
          pagination={{
            pageSize: SCHEDULED_PAGE_SIZE,
            current: page,
            onChange: setPage,
            hideOnSinglePage: true,
            size: "mini",
          }}
          noDataElement={t("admin.statistics.scheduled_no_data")}
        />
      </div>

      <Modal
        title={t("admin.statistics.scheduled_edit")}
        visible={!!editingPost}
        onCancel={() => setEditingPost(null)}
        footer={null}
        unmountOnExit
      >
        <Form form={form} layout="vertical" onSubmit={handleSaveEdit}>
          <Form.Item
            label={t("home_page.post_title_label")}
            field="heading"
            rules={[{ required: true, message: t("notification_messages.post_title_empty") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t("home_page.post_content_label")}
            field="content"
            rules={[{ required: true, message: t("notification_messages.post_content_empty") }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          <div className="statistics__schedule-pickers">
            <Form.Item
              label={t("home_page.schedule_date")}
              field="schedule_date"
              rules={[{ required: true, message: t("home_page.schedule_datetime_required") }]}
            >
              <DatePicker
                format="MMMM D, YYYY"
                style={{ width: "100%" }}
                disabledDate={disablePastDates}
                getPopupContainer={() => document.body}
              />
            </Form.Item>
            <Form.Item
              label={t("home_page.schedule_time")}
              field="schedule_time"
              rules={[{ required: true, message: t("home_page.schedule_datetime_required") }]}
            >
              <TimePicker
                use12Hours
                format="h:mm A"
                style={{ width: "100%" }}
                disableConfirm
                step={{ hour: 1, minute: 15 }}
                getPopupContainer={() => document.body}
                scrollSticky
              />
            </Form.Item>
            <Form.Item
              label={t("home_page.schedule_timezone")}
              field="schedule_timezone"
              rules={[{ required: true, message: t("home_page.schedule_datetime_required") }]}
            >
              <Select
                showSearch
                placeholder={t("home_page.schedule_timezone_placeholder")}
                options={timezoneOptions}
                style={{ width: "100%" }}
                filterOption={(input, option) => {
                  const opt = option as { value?: string; label?: string };
                  const haystack = `${opt.value ?? ""} ${opt.label ?? ""}`.toLowerCase();
                  return haystack.includes(input.toLowerCase());
                }}
                getPopupContainer={() => document.body}
              />
            </Form.Item>
          </div>
          <Button
            htmlType="submit"
            shape="round"
            loading={updateMutation.isPending}
            style={{
              color: getButtonTextColor(),
              backgroundColor: getButtonColor(),
            }}
          >
            {t("common.save_button")}
          </Button>
        </Form>
      </Modal>
    </div>
  );
};
